// The shared place database (TaskPogoPlace) layered in front of the external
// geocoder. "Lookup place" resolves a name through locatePlaceName: a stored
// place wins outright (this is where the owner's curated domain knowledge --
// PNW anchorage positions and the like -- overrides a geocoder that has been
// wrong about them), a miss falls through to Photon and hands the answer back
// for the CALLER to store, and a manual map correction
// (recordManualPlacement) updates the shared row so everyone gets the fix.
//
// Why the caller: a geocoder hit near a place that already exists is how
// duplicate rows are born (prod has two Victoria, Causeway Marina), and the
// only surface that can ask about it is the one the user is looking at. So
// locatePlaceName reports and useRouteLocator writes -- see locatePlaceName's
// own header for the marker it reports with.
//
// The name box's typeahead reads the same two tiers, in the same priority
// order, through three functions instead of one:
//
//   suggestPlaces       -- stored rows only, from an in-memory copy of the
//                          table. Instant, and always ranked first.
//   suggestRemotePlaces -- Photon candidates, debounced by the caller.
//   mergeSuggestions    -- pure: stored first, Photon appended, deduped.
//
// Contract inherited from geocodeName and relied on by useRouteLocator:
// resolves to { lat, lon } or null, never throws. The database is an
// accelerator, not a dependency -- an AppSync outage reads as a miss and the
// external geocoder still answers. Suggestions are the same bargain one level
// up: either tier failing degrades to the other, silently.
//
// The gazetteer EDITOR (/me/places) is the exception, and its writers sit in
// their own section at the bottom of this file: there the write is the user's
// action rather than a side effect of one, so those functions throw.
//
// One crack in the swallow-everything bargain: recordManualPlacement takes an
// optional `onRejected`. Since a bound waypoint now takes its row's position on
// every read, a writeback that never lands doesn't merely fail to share the
// correction -- it lets the next load undo it. The write is still
// fire-and-forget (returns immediately, never throws, never blocks the
// gesture); the callback only lets a caller that cares say so out loud.

import { geocodeName, suggestGeocode } from './geocode.mjs';
import {
  NewPlaceName,
  createPlace,
  deletePlace,
  getPlace,
  listPlaces,
  placesByNameKey,
  updatePlace,
} from './Database.mjs';
import { isPlaced, nameOwnsPosition } from '../fleet-shared/TripRoute.mjs';
import {
  indexPlaces,
  pickPlace,
  normalizePlaceName,
  FACILITY_FIELDS,
} from '../fleet-shared/PlaceFields.mjs';

// Re-exported from their fleet-shared home so this module stays the one import
// every app-side caller needs for the place database. Both moved there for the
// same reason: a caller outside the browser must key and rank rows identically
// and cannot load this file (Database.mjs pulls in aws-amplify) --
// scripts/backfill-places.mjs for the key, and the trip weather mail Lambda,
// which builds its own index from rows it fetched itself, for the index.
export { normalizePlaceName, indexPlaces };

// nameKey -> { expiresAt, promise }. The PROMISE is cached (weather.mjs's
// cwfCache pattern) so concurrent locates of one name share a single lookup
// instead of racing past an empty cache.
const PLACE_TTL_MS = 10 * 60 * 1000;
const placeCache = new Map();

// The full gazetteer for suggestion filtering: { expiresAt, promise } or null.
// Promise-cached like placeCache so a burst of keystrokes shares one fetch.
// There is no prefix index on the table (placesByNameKey is exact-match), so
// suggestions filter this list client-side -- which also buys substring
// matching. Writes invalidate it so corrections show up immediately.
let allPlacesCache = null;

// nameKey -> { expiresAt, promise } for the Photon tier. Keyed by the
// NORMALIZED query alone, so backspacing to a prefix you already typed -- a
// large share of real typeahead traffic -- is usually free. "Usually", not
// "always": an in-flight prefix request that the next keystroke aborts is
// deleted from the cache (aborts are never cached, below), so backspacing to a
// prefix whose request never completed does re-fetch. The key deliberately
// omits `limit`: every caller passes REMOTE_SUGGEST_LIMIT, and a second call
// site wanting a different count would need to fold it into the key first, or
// it would get this entry's count back.
const remoteSuggestCache = new Map();

export function _resetPlacesCacheForTests() {
  placeCache.clear();
  allPlacesCache = null;
  remoteSuggestCache.clear();
}

// The stored row for a normalized key, or null. Never throws -- a database
// failure is a cache miss, not an error.
async function findPlace(key) {
  try {
    return pickPlace(await placesByNameKey(key));
  } catch {
    return null;
  }
}

// The stored row a waypoint is BOUND to, or null when the id no longer
// resolves (any authenticated user may delete any row). Same never-throws
// bargain as findPlace: callers fall back to the name.
async function findPlaceById(id) {
  try {
    return (await getPlace(id)) || null;
  } catch {
    return null;
  }
}

// Drop-in replacement for geocodeName: { lat, lon, placeId } | null, never
// throws. A stored place skips the geocoder; a miss geocodes and answers from
// the geocoder without storing anything.
//
// `placeId` is the row the answer came from, so the caller can BIND the
// waypoint to it rather than just copying its coordinates. It is null for a
// fresh geocoder hit -- and THAT NULL IS THE CALLER'S CUE. It marks exactly the
// case that used to create a row here, silently, before anyone could be asked
// about it; useRouteLocator now partitions on it and either calls
// recordPickedPlace or puts the question to the user (NearbyPlacesModal).
//
// The write moved out rather than growing an option because this function is
// PROMISE-CACHED by name: an option would not be part of the cache key, so the
// second caller for a name would silently inherit the first one's choice. And
// a fire-and-forget write buried under a cache is not something a dialog can
// sit in front of.
//
// One behaviour change worth knowing: `locate` drops a result whose name was
// edited mid-lookup, so a renamed target now creates nothing. It used to create
// a row for the abandoned name regardless.
export async function locatePlaceName(name) {
  const key = normalizePlaceName(name);
  if (!key) return null;

  const cached = placeCache.get(key);
  if (cached && Date.now() < cached.expiresAt) return cached.promise;

  const promise = (async () => {
    const place = await findPlace(key);
    if (place) return { lat: place.lat, lon: place.lon, placeId: place.id };

    const position = await geocodeName(name);
    // A geocoder miss stores nothing: null rows would pin "not found"
    // forever, while retrying is free.
    if (!position) return null;

    // No row, and no id to bind to. The caller decides whether this name
    // becomes a place (recordPickedPlace) or binds to one that is already
    // there -- see the header. Storing it here would be a write nobody could
    // be asked about, which is how two rows for one marina get created.
    return { ...position, placeId: null };
  })();

  // Only positions are worth caching: a null (geocoder miss) should retry on
  // the next click, and geocodeName never rejects so the error arm is just
  // symmetry with the pattern.
  promise.then(
    (position) => {
      if (!position) placeCache.delete(key);
    },
    () => placeCache.delete(key)
  );
  placeCache.set(key, { expiresAt: Date.now() + PLACE_TTL_MS, promise });
  return promise;
}

// A manual map placement (marker drag, tap-to-place) of a NAMED point
// corrects the shared row -- unless that row is curated ground truth, which
// no ordinary map edit may overwrite. Fire-and-forget: returns immediately,
// never throws, never blocks the gesture. autoName points (coordinate-
// generated names) and unnamed points are not places and are never stored.
//
// `placeId` is the waypoint's gazetteer binding when it has one, and it wins
// over the name: the drag corrects the row the point is actually bound to,
// which is the only correct answer once two rows share a name (a state
// pickPlace tolerates by design). A dangling id falls back to the name so a
// deleted row can't strand the correction.
//
// `onRejected(reason, row)` is how a caller HEARS about the two ways this can
// decline to write: 'curated' (policy -- the row is ground truth) and 'failed'
// (the mutation threw). Optional, and omitting it restores the old behaviour
// exactly, which is why recordCommittedRename passes nothing.
//
// It exists because silence stopped being safe. A waypoint bound to a row now
// takes that row's position on every read (resolveRouteAgainstPlaces), so a
// drag whose writeback never lands is UNDONE on the next load rather than
// simply not shared -- the gesture appears to work and is quietly reverted.
// The trip map checks `curated` synchronously off its loaded index and refuses
// the drag before it starts; this callback is the backstop for what that index
// is too stale to know, and the only signal at all for a failed write.
export function recordManualPlacement({ name, autoName, lat, lon, placeId, onRejected }) {
  const reject = (reason, row) => {
    try {
      onRejected?.(reason, row);
    } catch {
      // A reporting callback must never turn into a second failure.
    }
  };

  const key = normalizePlaceName(name);
  if (autoName || !key) return;

  (async () => {
    const bound = placeId ? await findPlaceById(placeId) : null;
    const existing = bound || (await findPlace(key));
    if (existing?.curated) {
      reject('curated', existing);
      return;
    }

    let id = existing?.id || null;
    if (existing) {
      await updatePlace({ id: existing.id, name: name.trim(), lat, lon, source: 'user' });
    } else {
      const created = await createPlace({
        nameKey: key,
        name: name.trim(),
        lat,
        lon,
        source: 'user',
        curated: false,
      });
      id = created?.id || null;
    }

    // A re-locate in this session should see the correction, not the entry
    // this cache remembers from before the gesture -- and the suggestion
    // list should offer the corrected position, not the stale one. The id
    // rides along so a locate right after a tap binds instead of re-reading.
    allPlacesCache = null;
    placeCache.set(key, {
      expiresAt: Date.now() + PLACE_TTL_MS,
      promise: Promise.resolve({ lat, lon, placeId: id }),
    });
  })().catch(() => reject('failed'));
}

// Suggestions can't fire on every character -- one letter matches half the
// gazetteer -- so nothing is fetched or offered below this many characters.
const SUGGEST_MIN_CHARS = 2;

// The stored tier's cap and the merged list's. LOCAL < MERGED is load-bearing
// -- see mergeSuggestions.
const LOCAL_SUGGEST_LIMIT = 8;
const MERGED_SUGGEST_LIMIT = 10;

// The cached gazetteer, or null on failure. Failures aren't cached: the next
// keystroke retries, same reasoning as the geocoder-miss rule above.
function fetchAllPlaces() {
  if (allPlacesCache && Date.now() < allPlacesCache.expiresAt) {
    return allPlacesCache.promise;
  }
  const promise = listPlaces().catch(() => {
    allPlacesCache = null;
    return null;
  });
  allPlacesCache = { expiresAt: Date.now() + PLACE_TTL_MS, promise };
  return promise;
}

// The gazetteer as the two lookups TripRoute's resolveRouteAgainstPlaces
// needs: `byId` for the durable placeId binding, `byKey` for the name
// bootstrap that binds routes written before that key existed. `byKey` is
// resolved through pickPlace, so duplicate names are decided here rather than
// by whichever row the caller happened to see first.
//
// Never throws, and never rejects: a failure resolves to EMPTY maps, and
// resolveRouteAgainstPlaces treats an empty index as "the gazetteer had
// nothing to say" and leaves the route alone. That is deliberate -- the
// alternative, clearing every binding whenever the network hiccups, is far
// worse than skipping one overlay.
//
// This shares allPlacesCache (10 min) with suggestPlaces, so a trip page and
// the typeahead cost one table read between them rather than two. It is still
// a whole-table read: see README, the fix if the gazetteer ever outgrows it is
// a projection here rather than a new query pattern.
export async function loadPlaceIndex() {
  const places = await fetchAllPlaces();
  if (!places) return { byId: new Map(), byKey: new Map() };
  return indexPlaces(places);
}

// Stored places matching a partial name, for the typeahead: prefix matches
// first, then substring matches, one row per nameKey (pickPlace's winner).
// Within each tier, curated rows lead and each run is alphabetical -- a row
// somebody vouched for outranks a geocoder leftover with a similar name.
// Resolves to [] for short queries and on any failure -- suggestions are an
// accelerator, never an error.
export async function suggestPlaces(query, { limit = LOCAL_SUGGEST_LIMIT } = {}) {
  const key = normalizePlaceName(query);
  if (key.length < SUGGEST_MIN_CHARS) return [];

  try {
    const places = await fetchAllPlaces();
    if (!places) return [];

    const byKey = new Map();
    for (const place of places) {
      // indexPlaces' fallback: a row written without a nameKey still has a
      // name, and grouping on undefined would throw at startsWith below.
      // A row without a NAME is skipped outright -- it would throw in the
      // localeCompare tiebreaker (blanking the whole batch via the catch)
      // and a suggestion with nothing to render isn't one.
      const nameKey = place?.nameKey || normalizePlaceName(place?.name);
      if (!nameKey || !place.name) continue;
      if (!byKey.has(nameKey)) byKey.set(nameKey, []);
      byKey.get(nameKey).push(place);
    }

    const prefix = [];
    const substring = [];
    for (const [nameKey, rows] of byKey) {
      if (nameKey.startsWith(key)) prefix.push(pickPlace(rows));
      else if (nameKey.includes(key)) substring.push(pickPlace(rows));
    }
    // `=== true` because GraphQL serves curated as true/false/null.
    const byRank = (a, b) =>
      (b.curated === true) - (a.curated === true) || a.name.localeCompare(b.name);
    return [...prefix.sort(byRank), ...substring.sort(byRank)].slice(0, limit);
  } catch {
    return [];
  }
}

// The Photon tier starts a character later than the stored one: one and two
// characters match tens of thousands of OSM features, and skipping them
// removes the two highest-frequency fires from every naming session.
const SUGGEST_REMOTE_MIN_CHARS = 3;
const REMOTE_SUGGEST_LIMIT = 5;
const REMOTE_CACHE_MAX = 200;

// Photon candidates for a partial name, in Photon's order. Always an array --
// a failure is [] here, because a typeahead has nothing useful to say about
// one. Never throws.
export async function suggestRemotePlaces(query, { limit = REMOTE_SUGGEST_LIMIT, signal } = {}) {
  const key = normalizePlaceName(query);
  if (key.length < SUGGEST_REMOTE_MIN_CHARS) return [];

  const cached = remoteSuggestCache.get(key);
  if (cached && Date.now() < cached.expiresAt) return (await cached.promise) || [];

  // The cached promise carries whichever box's `signal` started it, so a second
  // reader awaiting this entry rides the initiator's abort: if the first box
  // aborts (every keystroke does), the shared promise resolves null and the
  // second reader shows no remote tier until its next keystroke. Unreachable
  // today -- a user types one box at a time and cancelRemote() serializes a
  // single box's requests, so two live readers of one normalized query never
  // co-occur -- but a future batch caller (e.g. "locate all") sharing this
  // cache would need its own signal, not this one.
  const promise = suggestGeocode(query, { limit, signal });

  // A successful empty answer is a fact worth keeping (Photon really doesn't
  // know "asdfgh"); a null -- failure, timeout, or the caller aborting mid
  // keystroke -- is not, and caching it would blank the tier for ten minutes
  // or let one abort poison every retype of that name.
  promise.then(
    (places) => {
      if (!places) remoteSuggestCache.delete(key);
    },
    () => remoteSuggestCache.delete(key)
  );

  // Oldest insertion out first: Map preserves insertion order, so the first
  // key is the oldest. Bounds a cache that would otherwise grow with every
  // distinct prefix a user ever types.
  if (remoteSuggestCache.size >= REMOTE_CACHE_MAX) {
    remoteSuggestCache.delete(remoteSuggestCache.keys().next().value);
  }
  remoteSuggestCache.set(key, { expiresAt: Date.now() + PLACE_TTL_MS, promise });

  return (await promise) || [];
}

// Stored places outrank Photon absolutely -- local knowledge is the whole
// point of the table -- so the merge is an APPEND, never an interleave. That
// is load-bearing for the keyboard: the caller holds an active-option index
// into this list, and remote results land while the user is already arrowing
// through it. Because every local row keeps its index when remote arrives,
// the index never has to be adjusted, and an arrowed-to row can't change
// underfoot between ArrowDown and Enter.
export function mergeSuggestions(local = [], remote = [], { limit = MERGED_SUGGEST_LIMIT } = {}) {
  // Nothing to append: hand back the same array so React can skip the render.
  if (!remote || remote.length === 0) return local;

  // suggestPlaces caps at LOCAL_SUGGEST_LIMIT and 8 < 10, so slicing the
  // concatenation below can only ever trim the remote tail. That inequality
  // is the entire reason the cap is safe to apply after the append.
  const seen = new Set(local.map((place) => place.nameKey || normalizePlaceName(place.name)));

  const kept = [];
  for (const place of remote) {
    const key = normalizePlaceName(place.name);
    // Adding as we go also collapses Photon's own duplicates -- it routinely
    // returns a node and a way for one village.
    if (seen.has(key)) continue;
    seen.add(key);
    kept.push(place);
  }

  return [...local, ...kept].slice(0, limit);
}

// A picked Photon suggestion enters the shared database, so the next user
// gets it from the stored tier and the owner has a row to override with
// `curated: true`. Fire-and-forget, never throws (mirrors
// recordManualPlacement).
//
// CREATE ONLY, never update. If a row existed, the stored tier would have
// offered it and mergeSuggestions would have deduped the Photon copy away --
// so reaching this write means the name is genuinely unknown. Keeping it
// create-only is also what makes it safe: recordManualPlacement stamps
// `source: 'user'`, and updating here would silently downgrade somebody's
// hand-correction back to a geocoder guess.
//
// Only a PICK writes. Merely displaying a suggestion writes nothing: naming
// one waypoint shows a dozen-odd candidates on three continents, and
// suggestPlaces reads the whole table into memory, so storing them all would
// degrade every future suggestion in exchange for rows nobody chose.
export function recordPickedPlace({ name, lat, lon }) {
  const key = normalizePlaceName(name);
  if (!key) return;

  (async () => {
    if (await findPlace(key)) return;

    const created = await createPlace({
      nameKey: key,
      name: name.trim(),
      lat,
      lon,
      source: 'geocode',
      curated: false,
    });

    // The name the user just committed belongs in the next suggestion list,
    // and a later "Lookup place" for it shouldn't re-ask anyone.
    allPlacesCache = null;
    placeCache.set(key, {
      expiresAt: Date.now() + PLACE_TTL_MS,
      promise: Promise.resolve({ lat, lon, placeId: created?.id || null }),
    });
  })().catch(() => {});
}

// A committed rename records name -> position when the pin SURVIVES the
// rename: the first-naming exemption in nameOwnsPosition (an unnamed or
// auto-named point keeps its pin), which is exactly a user asserting a name
// for a position they placed by hand. Every other rename of a placed point
// unplaces it instead, so there is no position to assert. Fire-and-forget;
// curated rows stay protected inside recordManualPlacement.
//
// The decision to record is judged against `previousWaypoint` (the focus-time
// snapshot -- the point as it was before the session's typing), but the
// position recorded comes from `currentWaypoint`: a pin dragged WHILE its name
// box is focused moves the live point, and the shared row should get where the
// pin actually ended up, not the pre-drag snapshot. `currentWaypoint` defaults
// to `previousWaypoint` for callers with no separate live copy (the map modal,
// whose rename is discrete and can't be mid-drag).
export function recordCommittedRename(previousWaypoint, newName, currentWaypoint = previousWaypoint) {
  if (!isPlaced(previousWaypoint)) return;
  if (nameOwnsPosition(previousWaypoint)) return;
  if (!normalizePlaceName(newName)) return;
  const placed = isPlaced(currentWaypoint) ? currentWaypoint : previousWaypoint;
  recordManualPlacement({
    name: newName,
    lat: placed.lat,
    lon: placed.lon,
  });
}

// ***** Deliberate writes: the editor (/me/places), and import *****
//
// Everything above this line is INCIDENTAL writing: a trip edit happens to
// teach the database something, fire-and-forget, and a failure is invisible
// because the user was doing something else. Below it the write IS the user's
// action. The editor's writers throw and the save queue reports it; import's
// (`ensurePlaces`) reports a count instead, because the places are a side
// effect of an import whose real payload is the entries.
//
// They live here rather than calling Database.mjs directly for one reason: they
// have to invalidate this module's caches. Exporting an invalidator instead
// would put that burden on every future caller, and the one who forgets leaves
// a trip page geocoding against a name the editor renamed an hour ago.

// The editor is the only writer that can move a row BETWEEN name keys or
// remove it outright, so it can't do what recordManualPlacement does and prime
// placeCache with the new position -- the OLD key's cached answer is now a lie
// and there is nothing to replace it with. Clearing outright is the version
// that can't be got wrong. Editing is rare and deliberate; the cost is at most
// one re-lookup per name later in the session.
//
// remoteSuggestCache is deliberately untouched: it holds Photon's answers,
// which no local edit changes, and mergeSuggestions dedupes against the stored
// tier when it merges rather than when it caches.
function invalidateAfterEdit() {
  allPlacesCache = null;
  placeCache.clear();
}

// The editor's list read. Unlike fetchAllPlaces -- the typeahead's, where a
// failure means "no suggestions" and the geocoder still answers -- this THROWS:
// the places page has nothing to show if it fails and must say so rather than
// render an empty map that reads as an empty database.
//
// The result primes the suggestion cache, because it is the same list.
export async function loadAllPlaces() {
  const places = await listPlaces();
  allPlacesCache = { expiresAt: Date.now() + PLACE_TTL_MS, promise: Promise.resolve(places) };
  return places;
}

// The map's "+": a stub row at the tapped coordinate, so the edit page has a
// real id to autosave against (the same create-then-edit flow as
// createTripWithUI). `source: 'user'` -- a hand-dropped pin is a manual
// placement, not a geocoder answer.
export async function createPlaceAt({ lat, lon, name = NewPlaceName }) {
  const created = await createPlace({
    nameKey: normalizePlaceName(name),
    name,
    lat,
    lon,
    source: 'user',
    curated: false,
  });
  invalidateAfterEdit();
  return created;
}

// An edit-page autosave flush. `input` is buildEditedFields' payload plus the
// id; the page derives `nameKey` from `name` before it gets here, so a rename
// arrives with both.
export async function savePlaceEdit(input) {
  const saved = await updatePlace(input);
  invalidateAfterEdit();
  return saved;
}

export async function removePlace(id) {
  await deletePlace(id);
  invalidateAfterEdit();
}

// Make sure the target environment has a row for every place an imported file
// refers to, and hand back the index to bind its routes against.
//
// CREATE-ONLY. A name that already has a row is left completely alone -- no
// update, no move, not even a touch. Three reasons, and all three matter:
// the file's copy may be a stale snapshot from another environment; the
// existing row may be curated, which is precisely the state that must not be
// overwritten; and create-only is what makes re-importing the same file a
// no-op instead of a slow rewrite of the shared table. There is deliberately
// no code path here that calls updatePlace.
//
// NEVER THROWS. TaskPogoPlace rows are a bonus on top of an import whose real
// payload is the entries, so a failure is counted and reported, never fatal.
// The caller shows the count; the routes still draw from their snapshots, and
// `scripts/backfill-places.mjs` can fill the gap later.
//
// The read goes through loadAllPlaces (which throws) rather than
// loadPlaceIndex (which resolves to EMPTY maps on failure). That is the whole
// ballgame: an empty index is indistinguishable from an empty table, so
// treating a failed read as "nothing exists yet" would create a duplicate row
// for every name in the file -- in a table shared by every user. A failed read
// means we write NOTHING and return an empty index, which makes
// resolveRouteAgainstPlaces a no-op downstream.
export async function ensurePlaces(rows, { onProgress } = {}) {
  const empty = { byId: new Map(), byKey: new Map() };

  // Re-derive the key here rather than trusting the caller's. PlaceIO derives
  // it too; this is the second of the two derivations, because a row keyed any
  // other way is one placesByNameKey can never find again.
  const wanted = new Map();
  for (const row of rows || []) {
    const nameKey = normalizePlaceName(row?.name);
    if (!nameKey || wanted.has(nameKey)) continue;
    wanted.set(nameKey, { ...row, nameKey, name: String(row.name).trim() });
  }
  if (wanted.size === 0) {
    // Still worth an index: a same-environment import binds by id off it.
    return { created: 0, failed: 0, failures: [], index: await loadPlaceIndex() };
  }

  let existing;
  try {
    existing = await loadAllPlaces();
  } catch (err) {
    console.warn('Could not read the place list; imported routes will keep their own positions.', err);
    return {
      created: 0,
      failed: wanted.size,
      failures: [...wanted.values()].map((row) => ({ row, error: err })),
      index: empty,
    };
  }

  const index = indexPlaces(existing);
  const missing = [...wanted.values()].filter((row) => !index.byKey.has(row.nameKey));

  const failures = [];
  let created = 0;
  for (let i = 0; i < missing.length; i++) {
    const row = missing[i];
    try {
      // Assembled key by key from a fixed list, never spread from the file:
      // AppSync rejects an entire mutation for one unknown attribute, so a
      // leaked `id` or `__typename` would turn every place write into a
      // failure the user only ever sees as a count.
      const place = await createPlace({
        nameKey: row.nameKey,
        name: row.name,
        lat: row.lat,
        lon: row.lon,
        source: row.source === 'geocode' ? 'geocode' : 'user',
        ...(typeof row.curated === 'boolean' ? { curated: row.curated } : { curated: false }),
        ...facilityColumns(row),
        ...(typeof row.friendlyName === 'string' && row.friendlyName.trim() !== ''
          ? { friendlyName: row.friendlyName }
          : {}),
        ...(typeof row.notes === 'string' && row.notes.trim() !== '' ? { notes: row.notes } : {}),
      });
      created += 1;
      // Patched in rather than re-read: listPlaces is an eventually-consistent
      // Scan, so a row created a moment ago may not come back yet -- and the
      // rows we just made are exactly the ones the caller needs to bind to.
      index.byId.set(place.id, place);
      if (!index.byKey.has(row.nameKey)) index.byKey.set(row.nameKey, place);
    } catch (err) {
      console.warn(`Could not add "${row.name}" to the place list.`, err);
      failures.push({ row, error: err });
    }
    onProgress?.(i + 1, missing.length, 'places');
  }

  // Only when something actually landed: the caches are still accurate for a
  // run that wrote nothing, which is the common case on a re-import.
  if (created > 0) invalidateAfterEdit();

  return { created, failed: failures.length, failures, index };
}

// What to tell the user when some of an import's places didn't land, or null
// when they all did. Names the consequence rather than the mechanism: the
// import worked, and what they lost is the pins following later corrections.
export function placesFailedNotice(failed) {
  if (!failed) return null;
  const noun = failed === 1 ? 'place' : 'places';
  return (
    `Imported. ${failed} ${noun} could not be added to the shared place list, ` +
    "so some route pins won't follow later corrections."
  );
}

// The facility booleans a row actually states. A missing one is left off the
// mutation entirely: null means "nobody has said", which is not the same claim
// as "no" (see FACILITY_FIELDS in PlaceFields.mjs).
function facilityColumns(row) {
  const columns = {};
  for (const field of FACILITY_FIELDS) {
    if (typeof row[field.key] === 'boolean') columns[field.key] = row[field.key];
  }
  return columns;
}

// Other rows sharing a name key. The editor warns with these rather than
// blocking the rename: duplicates are an anticipated state (pickPlace resolves
// them curated-first, then oldest), and a rename ONTO an existing name is the
// curation workflow itself -- claim the name, mark the row curated, outrank the
// geocoded copy. What the user needs is to see that the loser exists, not to be
// stopped. Never throws: a failed check just means no warning.
export async function findDuplicatePlaces(nameKey, excludeId) {
  const key = normalizePlaceName(nameKey);
  if (!key) return [];
  try {
    return (await placesByNameKey(key)).filter((place) => place.id !== excludeId);
  } catch {
    return [];
  }
}
